import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import OwnerLayout from '../../components/OwnerLayout';
import api from '../../utils/api';

export default function OwnerCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/api/owner/categories');
      setCategories(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return toast.error('Enter category name');
    setSaving(true);
    try {
      await api.post('/api/owner/categories', { name: newName.trim() });
      toast.success('Category added!');
      setNewName('');
      load();
    } catch { toast.error('Failed to add'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.patch(`/api/owner/categories/${id}`, { name: editName.trim() });
      toast.success('Updated!');
      setEditId(null);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? All items in this category will also be deleted.`)) return;
    try {
      await api.delete(`/api/owner/categories/${id}`);
      toast.success('Deleted!');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <OwnerLayout title="Categories">
      <div className="fade-in">
        {/* Add New */}
        <div className="card" style={{ marginBottom: 16 }}>
          <label className="label">Add New Category</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input"
              placeholder="e.g. Starters, Main Course..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>
              {saving ? <span className="spinner" style={{ width: 18, height: 18 }} /> : <Plus size={18} />}
            </button>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : categories.length === 0 ? (
          <div className="empty-state">
            <h3>No categories yet</h3>
            <p>Add categories like Starters, Main Course, Drinks etc.</p>
          </div>
        ) : (
          categories.map((cat, i) => (
            <div key={cat.id} className="card fade-in" style={styles.catCard}>
              {editId === cat.id ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="input"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleEdit(cat.id); if (e.key === 'Escape') setEditId(null); }}
                    autoFocus
                    style={{ flex: 1 }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => handleEdit(cat.id)}><Check size={14} /></button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}><X size={14} /></button>
                </div>
              ) : (
                <div style={styles.catRow}>
                  <div style={styles.catInfo}>
                    <span style={styles.catNum}>{i + 1}</span>
                    <span style={styles.catName}>{cat.name}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditId(cat.id); setEditName(cat.name); }}>
                      <Pencil size={14} />
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(cat.id, cat.name)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </OwnerLayout>
  );
}

const styles = {
  catCard: { marginBottom: 8 },
  catRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  catInfo: { display: 'flex', alignItems: 'center', gap: 12 },
  catNum: { width: 28, height: 28, background: '#fff0eb', color: '#FF6B35', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 },
  catName: { fontSize: '15px', fontWeight: '500', color: '#2C3E50' },
};
